import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, take, tap, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { version } from '../../../package.json';

// 👇 FCM (Firebase v8 – serwisy pomocnicze)
import { FcmService } from '../services/fcm/fcm.service';
import { FcmRegistrationService } from '../services/fcm/fcm-registration.service';
import { FcmPermissionService } from '../services/fcm/fcm-permission.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  // ===== FIELDS =====
  public login: string = null;
  private password: string = null;
  private basicAuth: string = null;
  public mainAccount = false;
  public userType:
    | 'DSLOCATE'
    | 'INTERNAL'
    | 'SERVICE_STANDARD'
    | 'STANDARD'
    | 'SERVICE_DSLOCATE';

  public authenticated = new BehaviorSubject<boolean>(false);

  private logoutUrl = environment.baseUrl + environment.api + 'auth/logout';
  private loginUrl = environment.baseUrl + environment.api + 'auth/login';
  private tempAuthUrl = environment.baseUrl + environment.api + 'auth/temporary';
  private basicAuthUrl = environment.baseUrl + environment.api + 'auth/basic_auth';
  private usersUrl = environment.baseUrl + environment.api + 'users' + environment.surfix;

  private jwtHelper: JwtHelperService = new JwtHelperService();

  private readonly thuliumLocalStorageKey =
    'datasystem2-7faeb0cc-a21c-4dd7-b046-a59d3d00f7dcchat-widget-state';

  // ostatni token FCM (do sprzątania przy logout)
  private lastFcmToken: string | null = null;

  // ===== CONSTRUCTOR =====
  constructor(
    private router: Router,
    private http: HttpClient,
    private translate: TranslateService,

    // FCM – wstrzyknięte „miękko”
    private fcm: FcmService,
    private fcmReg: FcmRegistrationService,
    private fcmPerm: FcmPermissionService
  ) {
    // Inicjalizacja FCM (bez paniki, jeśli się nie uda)
    try {
      this.fcm.init();
      this.fcm.registerServiceWorker();
    } catch (_) {}

    // Auto-odbudowa sesji, jak masz ciasteczko JWT
    if (this.getCookie('jwt')) {
      this.isAuthenticated()
        .pipe(take(1))
        .subscribe((authenticated) => {
          if (authenticated) {
            if (this.isDSLocateUser()) {
              this.initializeSession(
                { jwt: this.getCookie('jwt') },
                this.getCookie('locale')
              );
            } else {
              this.getBasicAuthByJwt().subscribe((res: any) => {
                this.initializeSession(
                  {
                    jwt: this.getCookie('jwt'),
                    basicAuth: res.basicAuth,
                  },
                  this.getCookie('locale')
                );
              });
            }
          }
        });
    }
  }

  // ===== STATIC (po polach, przed metodami instancyjnymi) =====
  private static appendDefaultHeaders(httpHeaders: HttpHeaders): HttpHeaders {
    httpHeaders = httpHeaders.append('Content-Type', 'application/json; charset=utf-8');
    httpHeaders = httpHeaders.append('Cache-Control', 'no-cache');
    httpHeaders = httpHeaders.append('App-Version', version);
    return httpHeaders;
  }

  // ===== METHODS =====

  private getSupervisor(login: string): void {
    this.http
      .get(this.usersUrl, { headers: this.getJwtAuthHeaders() })
      .subscribe((users: any[]) => {
        const found = users.find((user) => user.login === login);
        this.mainAccount = found ? !found.supervisor_id : false;
      });
  }

  private getBasicAuthByJwt(): Observable<any> {
    return this.http.get(this.basicAuthUrl, { headers: this.getJwtAuthHeaders() });
  }

  public isAuthenticated(): Observable<boolean> {
    const jwtToken = this.getCookie('jwt');
    if (this.basicAuth) {
      return of(this.isValidToken(jwtToken));
    } else if (this.isValidToken(jwtToken)) {
      this.userType = this.jwtHelper.decodeToken(jwtToken).userType;
      this.login = this.jwtHelper.decodeToken(jwtToken).userName;
      this.applyLoginToThulium(this.login);
      if (this.isDSLocateUser()) {
        return of(true);
      } else {
        return this.getBasicAuthByJwt().pipe(
          tap((res) => {
            this.basicAuth = res.basicAuth;
            const basicAuth = atob(res.basicAuth);
            this.login = basicAuth.split(':').slice(0, -1).join(':');
            this.password = basicAuth.split(':').slice(-1).join(':');
            return !!res.basicAuth;
          }),
          catchError(() => of(false))
        );
      }
    } else {
      return of(false);
    }
  }

  private applyLoginToThulium(login: string): void {
    const thuliumWidgetState = localStorage.getItem(this.thuliumLocalStorageKey);
    if (thuliumWidgetState) {
      const parsedState = JSON.parse(thuliumWidgetState);
      parsedState.customer.customerName = login;
      localStorage.setItem(this.thuliumLocalStorageKey, JSON.stringify(parsedState));
    }
  }

  public isValidToken(jwtToken?: string): boolean {
    if (jwtToken === undefined) { jwtToken = this.getCookie('jwt'); }
    try {
      return !this.jwtHelper.isTokenExpired(jwtToken);
    } catch {
      return false;
    }
  }

  public getToken(): string {
    return this.getCookie('jwt');
  }

  public authenticate(
    login: string,
    password: string,
    remindTomorrow: boolean = false,
    locale?: string
  ): Observable<any> {
    // remove when jwt will contain password
    this.login = login;
    this.password = password;

    return this.http
      .post(this.loginUrl, {
        user: { login, password, locale },
        remindTomorrow,
      })
      .pipe(
        map(
          (res: { jwt: string; basicAuth: string }) => this.initializeSession(res, locale, password),
          () => {
            this.authenticated.next(false);
            return of(false);
          }
        )
      );
  }

  private initializeSession(
    res: { jwt: string; basicAuth?: string },
    locale?: string,
    password?: string
  ) {
    const jwt = res.jwt;
    if (this.isValidToken(jwt)) {
      document.cookie = 'jwt=' + jwt + ';path=/;SameSite=Lax';
      this.userType = this.jwtHelper.decodeToken(jwt).userType;

      if (res.basicAuth) {
        this.basicAuth = res.basicAuth;
        const basicAuth = atob(res.basicAuth);
        this.login = basicAuth.split(':').slice(0, -1).join(':');
        this.password = basicAuth.split(':').slice(-1).join(':');
        this.applyLoginToThulium(this.login);
      }

      this.setLocale(locale);
      this.getSupervisor(this.login);
      this.authenticated.next(true);

      // FCM: przypnij token DOPOIERO po zalogowaniu (gdy jest JWT)
      this.tryRegisterFcmToken(jwt);

      return of(true);
    } else {
      return of(false);
    }
  }

  public authenticateTempUser(code: string) {
    return this.http
      .get(this.tempAuthUrl, { params: { code: code } })
      .pipe(tap((res: { jwt: string; basicAuth: string }) => this.initializeSession(res)));
  }

  public setLocale(locale?: string): void {
    if (!locale) locale = 'pl';
    sessionStorage.setItem('locale', locale);
    this.translate.use(locale);
  }

  public getUser() {
    return { login: this.login, password: this.password };
  }

  public getLocale(): 'pl' | 'en' {
    switch (this.getCookie('locale')) {
      case 'pl': return 'pl';
      case 'en': return 'en';
      default: return 'pl';
    }
  }

  public navigateToLogin(returnUrl?: string): void {
    this.router.navigate(['/login/login-screen'], { queryParams: { returnUrl } });
  }

  public getBasicAuthHeaders(): HttpHeaders {
    if (!this.basicAuth) {
      if (!this.getCookie('jwt')) { this.navigateToLogin(); }
      this.getBasicAuthByJwt().subscribe((res: any) => (this.basicAuth = res.basicAuth));
    }

    let basicAuthHeaders = new HttpHeaders();
    basicAuthHeaders = basicAuthHeaders.append('Authorization', this.getBasicAuth());
    basicAuthHeaders = AuthService.appendDefaultHeaders(basicAuthHeaders);
    return basicAuthHeaders;
  }

  public getBasicAuth(): string {
    return 'Basic ' + this.basicAuth;
  }

  public getJwtAuthHeaders(): HttpHeaders {
    const jwt = this.getCookie('jwt');
    if (!jwt || !this.isValidToken(jwt)) { this.navigateToLogin(); }

    let jwtHeaders = new HttpHeaders();
    // U Ciebie backend używa „gołego” JWT (nie Bearer) — zostawiam
    jwtHeaders = jwtHeaders.append('Authorization', jwt);
    jwtHeaders = AuthService.appendDefaultHeaders(jwtHeaders);
    return jwtHeaders;
  }

  getCookie(name: string): string {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].split('=');
      if (name === cookie[0].trim()) {
        return decodeURIComponent(cookie[1]);
      }
    }
    return null;
  }

  public isDSLocateUser(): boolean {
    return this.userType === 'DSLOCATE' || this.userType === 'SERVICE_DSLOCATE';
  }

  public isServiceStandardUser(): boolean {
    return this.userType === 'SERVICE_STANDARD';
  }

  logout(): void {
    // FCM: spróbuj wyrejestrować token zanim skasujesz JWT
    const jwtBeforeClear = this.getCookie('jwt');
    const token = this.readLastFcmToken();
    if (jwtBeforeClear && token) {
      this.fcmReg
        .unregisterToken(jwtBeforeClear, token)
        .toPromise()
        .catch(function () {});
    }

    const headers = this.getJwtAuthHeaders();
    document.cookie = 'jwt=;path=/';
    this.http.post(this.logoutUrl, {}, { headers }).subscribe(
      () => {},
      () => {},
      () => {
        this.authenticated.next(false);
        window.location.replace(environment.baseUrl);
        window.location.reload();
      }
    );
  }

  // ===== PRIVATES: FCM =====
  private async tryRegisterFcmToken(jwt: string): Promise<void> {
    try {
      // tylko jeśli przeglądarka wspiera i user ma już „Zezwalaj”
      if (this.fcmPerm.getBrowserPermission() !== 'granted') { return; }

      await this.fcm.registerServiceWorker();
      const token = await this.fcm.getToken();
      if (!token) { return; }

      this.lastFcmToken = token;
      try { localStorage.setItem('fcmToken', token); } catch (_) {}

      // Rejestracja tokenu dopiero po zalogowaniu (JWT w param.)
      await this.fcmReg.registerToken(jwt, token).toPromise();
    } catch (_) {
      // cicho — nie psujemy logowania przez FCM
    }
  }

  private readLastFcmToken(): string | null {
    if (this.lastFcmToken) { return this.lastFcmToken; }
    try { return localStorage.getItem('fcmToken'); } catch (_) { return null; }
  }
}
